// 文字渲染与双层叠加合成（文字拼豆 · 叠加图纸）
// 内核：文字 → 布尔掩码（1 像素 = 1 豆）；两层掩码 → 标准 pattern grid
import { findNearestColor } from './color.js'

// 可选字体：行书 / 毛笔 / 大气粗黑 / 宋体 / 像素体（全部 SIL OFL 1.1，可免费商用）
// google 字段存在时按需加载 Google Fonts（unicode-range 只下载用到的字形）
export const OVERLAY_FONTS = [
  { key: 'pixel', label: '像素体', family: '"Fusion Pixel", monospace', pixel: true },
  { key: 'zhimangxing', label: '志莽行书', family: '"Zhi Mang Xing", "KaiTi", "Kaiti SC", cursive', google: 'Zhi+Mang+Xing' },
  { key: 'mashanzheng', label: '马善政毛笔', family: '"Ma Shan Zheng", "KaiTi", "Kaiti SC", cursive', google: 'Ma+Shan+Zheng' },
  { key: 'huangyou', label: '庆科黄油体', family: '"ZCOOL QingKe HuangYou", "SimHei", "Microsoft YaHei", sans-serif', google: 'ZCOOL+QingKe+HuangYou' },
  { key: 'notoserif', label: '思源宋体（粗）', family: '"Noto Serif SC", serif', google: 'Noto+Serif+SC:wght@700', weight: 'bold' },
  { key: 'notosans', label: '思源黑体（粗）', family: '"Noto Sans SC", sans-serif', google: 'Noto+Sans+SC:wght@700', weight: 'bold' },
]

const PIXEL_NATIVE_PX = 12

// 底面文字三色渐变预设模板（上 → 中 → 下条纹；运行时映射到当前品牌色板中最接近的实色）
// 仅提供预设，不支持自定义编辑三色
export const GRADIENT_PRESETS = [
  { key: 'sunset', label: '落日熔金', colors: ['#FFD43B', '#FD7E14', '#E03131'] },
  { key: 'ocean', label: '海洋之心', colors: ['#74C0FC', '#1C7ED6', '#1864AB'] },
  { key: 'forest', label: '森林晨露', colors: ['#8CE99A', '#37B24D', '#2B8A3E'] },
  { key: 'violet', label: '紫罗兰梦', colors: ['#D0BFFF', '#9775FA', '#5F3DC4'] },
  { key: 'candy', label: '蜜桃糖果', colors: ['#FFC9DE', '#F06595', '#A61E4D'] },
  { key: 'goldred', label: '国潮红金', colors: ['#FFE066', '#F03E3E', '#C92A2A'] },
]

// 将预设模板的三色映射到品牌色板实色 id（返回 [上id, 中id, 下id]）
export function resolveGradientPreset(preset, brandColors) {
  return preset.colors.map((hex) => {
    const n = parseInt(hex.replace('#', ''), 16)
    const nearest = findNearestColor((n >> 16) & 255, (n >> 8) & 255, n & 255, brandColors)
    return nearest ? nearest.id : null
  })
}

// 已请求过加载的字体（避免重复插入 link / 重复 load）
const fontLoadPromises = new Map()

// 按需加载字体（Google Fonts 动态 link + Font Loading API），失败不阻塞（回退栈生效）
export function ensureFontLoaded(font, sampleText = '拼豆文字Ag') {
  if (!font) return Promise.resolve()
  if (fontLoadPromises.has(font.key)) return fontLoadPromises.get(font.key)

  const promise = (async () => {
    try {
      if (font.google && typeof document !== 'undefined') {
        const href = `https://fonts.googleapis.com/css2?family=${font.google}&display=swap`
        if (!document.querySelector(`link[href="${href}"]`)) {
          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = href
          document.head.appendChild(link)
        }
      }
      const familyName = font.family.split(',')[0].replace(/"/g, '').trim()
      const fontFace = `${font.weight || 'normal'} 32px "${familyName}"`
      // 8s 超时兜底：字体加载失败时用回退字体渲染，不阻塞用户操作
      await Promise.race([
        document.fonts?.load(fontFace, sampleText),
        new Promise((resolve) => setTimeout(resolve, 8000)),
      ])
    } catch {
      // ignore — fallback font stack will be used
    }
  })()
  fontLoadPromises.set(font.key, promise)
  return promise
}

// 像素字体路径：原生 12px 渲染 → 最近邻缩放（不糊边，1 像素 = 1 豆）
function renderPixelMask(text, font, charHeight) {
  const tmp = document.createElement('canvas')
  const tmpCtx = tmp.getContext('2d')
  tmpCtx.font = `${PIXEL_NATIVE_PX}px ${font.family}`
  const measured = tmpCtx.measureText(text)
  const textWidthPx = Math.max(
    1,
    Math.ceil(Math.abs(measured.actualBoundingBoxLeft || 0) + Math.abs(measured.actualBoundingBoxRight || measured.width))
  )
  tmp.width = textWidthPx
  tmp.height = PIXEL_NATIVE_PX
  tmpCtx.font = `${PIXEL_NATIVE_PX}px ${font.family}`
  tmpCtx.textBaseline = 'top'
  tmpCtx.fillStyle = '#000000'
  tmpCtx.fillText(text, measured.actualBoundingBoxLeft || 0, 0)

  const scale = charHeight / PIXEL_NATIVE_PX
  const w = Math.max(1, Math.round(textWidthPx * scale))
  const out = document.createElement('canvas')
  out.width = w
  out.height = charHeight
  const outCtx = out.getContext('2d')
  outCtx.imageSmoothingEnabled = false
  outCtx.drawImage(tmp, 0, 0, w, charHeight)
  const data = outCtx.getImageData(0, 0, w, charHeight).data

  const mask = new Uint8Array(w * charHeight)
  for (let i = 0; i < w * charHeight; i++) {
    mask[i] = data[i * 4 + 3] > 127 ? 1 : 0
  }
  return { w, h: charHeight, mask }
}

// 普通字体路径：高清渲染 → 平滑降采样（每格取平均 alpha），阈值 0.5 判定笔画格
function renderSmoothMask(text, font, charHeight) {
  const P = Math.max(64, charHeight * 8)
  const fontSpec = `${font.weight || 'normal'} ${P}px ${font.family}`

  const tmp = document.createElement('canvas')
  const tmpCtx = tmp.getContext('2d')
  tmpCtx.font = fontSpec
  const measured = tmpCtx.measureText(text)
  const left = Math.abs(measured.actualBoundingBoxLeft || 0)
  const right = Math.abs(measured.actualBoundingBoxRight || measured.width)
  const ascent = measured.actualBoundingBoxAscent || P * 0.8
  const descent = measured.actualBoundingBoxDescent || P * 0.25
  const wPx = Math.max(1, Math.ceil(left + right))
  const hPx = Math.max(1, Math.ceil(ascent + descent))

  tmp.width = wPx
  tmp.height = hPx
  tmpCtx.font = fontSpec
  tmpCtx.textBaseline = 'alphabetic'
  tmpCtx.fillStyle = '#000000'
  tmpCtx.fillText(text, left, ascent)

  const scale = charHeight / hPx
  const w = Math.max(1, Math.round(wPx * scale))
  const out = document.createElement('canvas')
  out.width = w
  out.height = charHeight
  const outCtx = out.getContext('2d')
  outCtx.imageSmoothingEnabled = true
  outCtx.imageSmoothingQuality = 'high'
  outCtx.drawImage(tmp, 0, 0, w, charHeight)
  const data = outCtx.getImageData(0, 0, w, charHeight).data

  const mask = new Uint8Array(w * charHeight)
  for (let i = 0; i < w * charHeight; i++) {
    mask[i] = data[i * 4 + 3] > 127 ? 1 : 0
  }
  return { w, h: charHeight, mask }
}

// 多行文字纵向堆叠：每行水平居中，行间距 = max(1, 行高 × 0.2)
function stackMasksVertical(masks, charHeight) {
  const gap = Math.max(1, Math.round(charHeight * 0.2))
  const w = Math.max(...masks.map((m) => m.w))
  const h = masks.reduce((sum, m) => sum + m.h, 0) + gap * (masks.length - 1)
  const mask = new Uint8Array(w * h)
  let yOffset = 0
  for (const m of masks) {
    const xOffset = Math.round((w - m.w) / 2)
    for (let y = 0; y < m.h; y++) {
      for (let x = 0; x < m.w; x++) {
        if (m.mask[y * m.w + x] === 1) {
          mask[(yOffset + y) * w + (xOffset + x)] = 1
        }
      }
    }
    yOffset += m.h + gap
  }
  return { w, h, mask }
}

// 渲染文字为布尔掩码：{ w, h, mask }（mask[i]=1 表示笔画格）
// 支持换行（\n）：每行独立渲染后水平居中堆叠；空行被忽略
export function renderTextMask(text, font, charHeight) {
  if (!text || !text.trim()) return null
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean)
  if (lines.length === 0) return null
  const renderLine = (line) =>
    font.pixel ? renderPixelMask(line, font, charHeight) : renderSmoothMask(line, font, charHeight)
  if (lines.length === 1) return renderLine(lines[0])
  return stackMasksVertical(lines.map(renderLine), charHeight)
}

// 描边可选颜色（10 种常用色；运行时映射到当前品牌色板实色，不支持自定义编辑）
export const OUTLINE_COLORS = [
  { key: 'black', label: '黑色', hex: '#000000' },
  { key: 'white', label: '白色', hex: '#FFFFFF' },
  { key: 'gray', label: '深灰', hex: '#495057' },
  { key: 'red', label: '红色', hex: '#E03131' },
  { key: 'blue', label: '蓝色', hex: '#1971C2' },
  { key: 'green', label: '绿色', hex: '#2F9E44' },
  { key: 'orange', label: '橙色', hex: '#F08C00' },
  { key: 'gold', label: '金黄', hex: '#FCC419' },
  { key: 'pink', label: '粉色', hex: '#D6336C' },
  { key: 'purple', label: '紫色', hex: '#7048E8' },
]

// 将描边预设色映射到品牌色板实色 id
export function resolveOutlineColor(hex, brandColors) {
  const n = parseInt(hex.replace('#', ''), 16)
  const nearest = findNearestColor((n >> 16) & 255, (n >> 8) & 255, n & 255, brandColors)
  return nearest ? nearest.id : null
}

// ===== 整体轮廓描边（欧氏距离变换，柔性圆角） =====

// 掩码外扩 margin 格（画布扩大，offset 相应前移）
function padMask(m, margin) {
  const w = m.w + margin * 2
  const h = m.h + margin * 2
  const mask = new Uint8Array(w * h)
  for (let y = 0; y < m.h; y++) {
    for (let x = 0; x < m.w; x++) {
      if (m.mask[y * m.w + x] === 1) mask[(y + margin) * w + (x + margin)] = 1
    }
  }
  return { w, h, mask, offsetX: (m.offsetX || 0) - margin, offsetY: (m.offsetY || 0) - margin }
}

// 一维平方距离变换（Felzenszwalb-Huttenlocher）
function dt1d(f, n) {
  const d = new Float64Array(n)
  const v = new Int32Array(n)
  const z = new Float64Array(n + 1)
  let k = 0
  v[0] = 0
  z[0] = -Infinity
  z[1] = Infinity
  for (let q = 1; q < n; q++) {
    let s = (f[q] + q * q - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k])
    while (s <= z[k]) {
      k--
      s = (f[q] + q * q - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k])
    }
    k++
    v[k] = q
    z[k] = s
    z[k + 1] = Infinity
  }
  k = 0
  for (let q = 0; q < n; q++) {
    while (z[k + 1] < q) k++
    d[q] = (q - v[k]) * (q - v[k]) + f[v[k]]
  }
  return d
}

// 二维欧氏平方距离变换：返回每格到最近 mask=1 格的距离平方
function edtSquared(m) {
  const { w, h, mask } = m
  const INF = 1e12
  const f = new Float64Array(w * h)
  for (let i = 0; i < w * h; i++) f[i] = mask[i] === 1 ? 0 : INF
  // 沿 x
  for (let y = 0; y < h; y++) {
    const row = f.slice(y * w, (y + 1) * w)
    const d = dt1d(row, w)
    f.set(d, y * w)
  }
  // 沿 y
  for (let x = 0; x < w; x++) {
    const col = new Float64Array(h)
    for (let y = 0; y < h; y++) col[y] = f[y * w + x]
    const d = dt1d(col, h)
    for (let y = 0; y < h; y++) f[y * w + x] = d[y]
  }
  return f
}

// 圆盘膨胀：到掩码距离 ≤ R 的格全部置 1（圆角，无方形膨胀的生硬直角）
function dilateSoft(m, R) {
  const ed = edtSquared(m)
  const out = new Uint8Array(m.w * m.h)
  for (let i = 0; i < out.length; i++) out[i] = ed[i] <= R * R ? 1 : 0
  return { w: m.w, h: m.h, mask: out, offsetX: m.offsetX, offsetY: m.offsetY }
}

// 圆盘腐蚀：到背景距离 > R 的格才保留
function erodeSoft(m, R) {
  const inv = new Uint8Array(m.w * m.h)
  for (let i = 0; i < inv.length; i++) inv[i] = m.mask[i] === 1 ? 0 : 1
  const ed = edtSquared({ w: m.w, h: m.h, mask: inv })
  const out = new Uint8Array(m.w * m.h)
  for (let i = 0; i < out.length; i++) out[i] = ed[i] > R * R ? 1 : 0
  return { w: m.w, h: m.h, mask: out, offsetX: m.offsetX, offsetY: m.offsetY }
}

// 填充内部空洞（从边界洪泛背景，到达不了的 0 格视为洞 → 填 1）
function fillHoles(m) {
  const { w, h, mask } = m
  const outside = new Uint8Array(w * h)
  const stack = []
  for (let x = 0; x < w; x++) { stack.push(x, (h - 1) * w + x) }
  for (let y = 0; y < h; y++) { stack.push(y * w, y * w + (w - 1)) }
  while (stack.length) {
    const i = stack.pop()
    if (outside[i] || mask[i] === 1) continue
    outside[i] = 1
    const x = i % w
    const y = (i - x) / w
    if (x > 0) stack.push(i - 1)
    if (x < w - 1) stack.push(i + 1)
    if (y > 0) stack.push(i - w)
    if (y < h - 1) stack.push(i + w)
  }
  const out = new Uint8Array(w * h)
  for (let i = 0; i < w * h; i++) out[i] = mask[i] === 1 || outside[i] === 0 ? 1 : 0
  return { w, h, mask: out, offsetX: m.offsetX, offsetY: m.offsetY }
}

// 合并底/顶两层文字掩码为一张内容掩码（top 位于相对 bottom 的 topX/topY 处）
export function unionMasks(bottom, top, topX = 0, topY = 0) {
  if (!top) return bottom
  const minX = Math.min(0, topX)
  const minY = Math.min(0, topY)
  const maxX = Math.max(bottom.w, topX + top.w)
  const maxY = Math.max(bottom.h, topY + top.h)
  const w = maxX - minX
  const h = maxY - minY
  const mask = new Uint8Array(w * h)
  for (let y = 0; y < bottom.h; y++)
    for (let x = 0; x < bottom.w; x++)
      if (bottom.mask[y * bottom.w + x] === 1) mask[(y - minY) * w + (x - minX)] = 1
  for (let y = 0; y < top.h; y++)
    for (let x = 0; x < top.w; x++)
      if (top.mask[y * top.w + x] === 1) mask[(y + topY - minY) * w + (x + topX - minX)] = 1
  return { w, h, mask, offsetX: minX, offsetY: minY }
}

// 整体轮廓柔性描边：基于传入的内容掩码（底+顶整体），
// 圆盘闭运算合并字缝 → 填充空洞 → 按欧氏距离取环：(gap, gap+thickness]，边缘圆润不生硬。
// 每次调用都从当前内容重新计算（勾选/取消勾选/任何参数变化都会触发重算）。
export function makeSilhouetteOutline(maskObj, thickness, charHeight, gap = 1) {
  const R = Math.max(2, Math.min(5, Math.round(charHeight * 0.12)))
  const pad = R + gap + thickness + 2
  let m = padMask(maskObj, pad)
  m = dilateSoft(m, R)
  m = erodeSoft(m, R)
  m = fillHoles(m)
  // 环：到轮廓的欧氏距离 d ∈ (gap + 0.5, gap + thickness + 0.5]
  const ed = edtSquared(m)
  const dMin = gap + 0.5
  const dMax = gap + thickness + 0.5
  const ring = new Uint8Array(m.w * m.h)
  for (let i = 0; i < ring.length; i++) {
    const d2 = ed[i]
    if (d2 > dMin * dMin && d2 <= dMax * dMax) ring[i] = 1
  }
  return { w: m.w, h: m.h, mask: ring, offsetX: m.offsetX, offsetY: m.offsetY }
}
// 叠加合成：优先级 重叠C > 顶字B > 底字A（或渐变三色） > 描边 > 空(null)
// topX/topY 为顶层掩码相对底层掩码左上角(含 pad 前)的偏移（可为负，顶层允许超出底层范围）
// colorA 可以是单色 id，也可以是 [上, 中, 下] 三个 id（三色上下条纹渐变）
// outline 为 dilateOutline 的产出（底字外扩一圈的环），outlineColor 为描边色 id
export function composeOverlay({ bottom, top, topX = 0, topY = 0, colorA, colorB, colorC, outline = null, outlineColor = null, pad = 1 }) {
  if (!bottom) return null

  // 计算各层并集的包围盒（描边环相对底字偏移 -1,-1）
  const minX = Math.min(0, top ? topX : 0, outline ? outline.offsetX : 0)
  const minY = Math.min(0, top ? topY : 0, outline ? outline.offsetY : 0)
  const maxX = Math.max(bottom.w, top ? topX + top.w : 0, outline ? outline.offsetX + outline.w : 0)
  const maxY = Math.max(bottom.h, top ? topY + top.h : 0, outline ? outline.offsetY + outline.h : 0)

  const width = maxX - minX + pad * 2
  const height = maxY - minY + pad * 2
  const bottomX = -minX + pad
  const bottomY = -minY + pad
  const tX = topX - minX + pad
  const tY = topY - minY + pad
  const oX = outline ? outline.offsetX - minX + pad : 0
  const oY = outline ? outline.offsetY - minY + pad : 0

  const grid = []
  for (let y = 0; y < height; y++) {
    const row = []
    for (let x = 0; x < width; x++) {
      const bx = x - bottomX
      const by = y - bottomY
      const inBottom = bx >= 0 && bx < bottom.w && by >= 0 && by < bottom.h && bottom.mask[by * bottom.w + bx] === 1

      let inTop = false
      if (top) {
        const tx = x - tX
        const ty = y - tY
        inTop = tx >= 0 && tx < top.w && ty >= 0 && ty < top.h && top.mask[ty * top.w + tx] === 1
      }

      let inOutline = false
      if (outline && outlineColor) {
        const ox = x - oX
        const oy = y - oY
        inOutline = ox >= 0 && ox < outline.w && oy >= 0 && oy < outline.h && outline.mask[oy * outline.w + ox] === 1
      }

      if (inBottom && inTop) row.push(colorC)
      else if (inTop) row.push(colorB)
      else if (inBottom) {
        // 三色渐变：按当前格在底字高度的相对位置分三段取色
        if (Array.isArray(colorA)) {
          row.push(colorA[Math.min(2, Math.floor((by / bottom.h) * 3))])
        } else {
          row.push(colorA)
        }
      } else if (inOutline) row.push(outlineColor)
      else row.push(null)
    }
    grid.push(row)
  }
  return { grid, width, height }
}

// 叠加色自动推荐：A、B 各半混合后加深，映射到品牌色板中最接近的实色
export function autoOverlapColorId(hexA, hexB, brandColors) {
  const parse = (hex) => {
    const n = parseInt(hex.replace('#', ''), 16)
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
  }
  const a = parse(hexA)
  const b = parse(hexB)
  const DARKEN = 0.72
  const r = Math.round(((a.r + b.r) / 2) * DARKEN)
  const g = Math.round(((a.g + b.g) / 2) * DARKEN)
  const bl = Math.round(((a.b + b.b) / 2) * DARKEN)
  const nearest = findNearestColor(r, g, bl, brandColors)
  return nearest ? nearest.id : null
}

// 由 grid 生成 legend 与 total（与 TextBeadModal 输出结构一致）
export function buildLegend(grid, brand) {
  const colorMap = new Map(brand.colors.map((c) => [c.id, c]))
  const legend = new Map()
  let total = 0
  for (const row of grid) {
    for (const id of row) {
      if (!id) continue
      const color = colorMap.get(id)
      const entry = legend.get(id) || { ...(color || { id, hex: '#cccccc', name: id }), count: 0 }
      entry.count += 1
      legend.set(id, entry)
      total += 1
    }
  }
  return { legend: Array.from(legend.values()), total }
}
